import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GraphTreeNode, UserNodeMastery } from '../../services/knowledgeGraphService';
import CategoryDrawer from './CategoryDrawer';
import './CategoryDetailSection.css';

interface Props {
    categoryNode: GraphTreeNode;
    masteryMap: Map<string, UserNodeMastery>;
    isPlacementOnly: boolean;
    lang: string;
}

const CEFR_COLORS: Record<string, string> = {
    A1: '#22c55e',
    A2: '#84cc16',
    B1: '#eab308',
    B2: '#f97316',
    C1: '#ef4444',
    C2: '#dc2626',
};

function cefrColor(level: string): string {
    return CEFR_COLORS[level] || '#94a3b8';
}

function scoreToCefr(score: number): string {
    if (score >= 0.9) return 'C2';
    if (score >= 0.78) return 'C1';
    if (score >= 0.62) return 'B2';
    if (score >= 0.45) return 'B1';
    if (score >= 0.25) return 'A2';
    return 'A1';
}

const CategoryDetailSection: React.FC<Props> = ({ categoryNode, masteryMap, isPlacementOnly, lang }) => {
    const { t } = useTranslation('knowledgeGraph');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerTitle, setDrawerTitle] = useState('');
    const [drawerDesc, setDrawerDesc] = useState<string | undefined>();
    const [drawerContent, setDrawerContent] = useState<React.ReactNode>(null);

    const getMastery = (nodeId: string) => {
        const m = masteryMap.get(nodeId);
        return m ? Number(m.masteryScore) : 0;
    };

    const masteryColor = (score: number) => cefrColor(scoreToCefr(score));

    const formatScore = (score: number) => {
        if (isPlacementOnly) return scoreToCefr(score);
        return `${Math.round(score * 100)}%`;
    };

    const openGroupDrawer = (node: GraphTreeNode) => {
        const label = node.labels?.[lang] || node.labels?.en || node.code;
        const groupMastery = getMastery(node.id);
        setDrawerTitle(label);
        setDrawerDesc(t('drawer.groupDesc', 'This skill group shows your mastery across individual topics. Click "Learn More" for detailed study materials.'));
        setDrawerContent(
            <div className="cat-drawer-content">
                <div className="cat-drawer-summary">
                    <span className="cat-drawer-summary__label">{t('mastery', 'Mastery')}</span>
                    <span className="cat-drawer-summary__score" style={{ color: masteryColor(groupMastery) }}>
                        {formatScore(groupMastery)}
                    </span>
                </div>
                {node.children && node.children.length > 0 && (
                    <div className="cat-drawer-leaves">
                        {node.children
                            .sort((a, b) => a.sortOrder - b.sortOrder)
                            .map(leaf => {
                                const leafMastery = getMastery(leaf.id);
                                const leafPct = Math.round(leafMastery * 100);
                                const leafLabel = leaf.labels?.[lang] || leaf.labels?.en || leaf.code;
                                const leafColor = masteryColor(leafMastery);
                                return (
                                    <div key={leaf.id} className="cat-drawer-leaf">
                                        <span className="cat-drawer-leaf__label">{leafLabel}</span>
                                        <div className="cat-drawer-leaf__bar">
                                            <div
                                                className="cat-drawer-leaf__fill"
                                                style={{ width: `${leafPct}%`, backgroundColor: leafColor }}
                                            />
                                        </div>
                                        <span className="cat-drawer-leaf__score">{formatScore(leafMastery)}</span>
                                    </div>
                                );
                            })}
                    </div>
                )}
            </div>
        );
        setDrawerOpen(true);
    };

    const categoryMastery = getMastery(categoryNode.id);
    const headerColor = masteryColor(categoryMastery);
    const categoryLabel = categoryNode.labels?.[lang] || categoryNode.labels?.en || categoryNode.code;

    return (
        <div className="cat-detail">
            <div className="cat-detail__header" style={{ borderLeftColor: headerColor }}>
                <span className="cat-detail__title">{categoryLabel}</span>
                <span className="cat-detail__score" style={{ color: headerColor }}>
                    {formatScore(categoryMastery)}
                </span>
            </div>

            <div className="cat-detail__groups">
                {categoryNode.children
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(group => (
                        <GroupNode
                            key={group.id}
                            node={group}
                            masteryMap={masteryMap}
                            isPlacementOnly={isPlacementOnly}
                            lang={lang}
                            onOpenDrawer={openGroupDrawer}
                        />
                    ))}
            </div>

            <CategoryDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={drawerTitle}
                description={drawerDesc}
            >
                {drawerContent}
            </CategoryDrawer>
        </div>
    );
};

interface GroupNodeProps {
    node: GraphTreeNode;
    masteryMap: Map<string, UserNodeMastery>;
    isPlacementOnly: boolean;
    lang: string;
    onOpenDrawer: (node: GraphTreeNode) => void;
}

const GroupNode: React.FC<GroupNodeProps> = ({ node, masteryMap, isPlacementOnly, lang, onOpenDrawer }) => {
    const getMastery = (nodeId: string) => {
        const m = masteryMap.get(nodeId);
        return m ? Number(m.masteryScore) : 0;
    };

    const formatScore = (score: number) => {
        if (isPlacementOnly) return scoreToCefr(score);
        return `${Math.round(score * 100)}%`;
    };

    const groupMastery = getMastery(node.id);
    const pct = Math.round(groupMastery * 100);
    const label = node.labels?.[lang] || node.labels?.en || node.code;
    const hasChildren = node.children && node.children.length > 0;
    const masteryColor = (score: number) => cefrColor(scoreToCefr(score));
    const groupColor = masteryColor(groupMastery);

    return (
        <div className="cat-detail__group">
            <div
                className={`cat-detail__group-header ${hasChildren ? 'cat-detail__group-header--clickable' : ''}`}
                onClick={() => hasChildren && onOpenDrawer(node)}
            >
                {hasChildren && (
                    <span className="cat-detail__chevron">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </span>
                )}
                <span className="cat-detail__group-label">{label}</span>
                <div className="cat-detail__group-bar">
                    <div
                        className="cat-detail__group-fill"
                        style={{ width: `${pct}%`, backgroundColor: groupColor }}
                    />
                </div>
                <span className="cat-detail__group-score">{formatScore(groupMastery)}</span>
            </div>
        </div>
    );
};

export default CategoryDetailSection;
